import { Users } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { FluentCard } from '@/components/layout/page-header'
import { getAvatarColor, getInitials } from '@/lib/avatar-colors'
import type { TopUserAnalytics } from '@/lib/api/types'

interface TopUsersTableProps {
  data: TopUserAnalytics[]
  loading?: boolean
  title?: string
  description?: string
}

export function TopUsersTable({
  data,
  loading,
  title = 'Top Users',
  description = 'Most active users in the selected period',
}: TopUsersTableProps) {
  if (loading) {
    return (
      <FluentCard>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
            <Users className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
          </div>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 animate-pulse">
              <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1">
                <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
                <div className="h-3 w-40 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
            </div>
          ))}
        </div>
      </FluentCard>
    )
  }

  // Safely calculate max count with fallback for undefined values
  const maxCount = Math.max(...data.map(u => u.count ?? 0), 1)

  return (
    <FluentCard>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
          <Users className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
        </div>
      </div>

      {data.length > 0 ? (
        <div className="space-y-3">
          {data.map((user, index) => (
            <div
              key={user.userId}
              className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50"
            >
              {/* Rank badge */}
              <div className="w-6 text-center">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  #{index + 1}
                </span>
              </div>

              {/* Avatar */}
              {(() => {
                const nameParts = user.userName.split(' ')
                const initials = getInitials(nameParts[0], nameParts[1])
                const color = getAvatarColor(user.userId || user.userName)
                return (
                  <Avatar className="h-10 w-10 rounded-full">
                    <AvatarImage src={user.userAvatar} alt={user.userName} className="rounded-full" />
                    <AvatarFallback className={`rounded-full text-sm font-medium ${color.bg} ${color.text}`}>
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                )
              })()}

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate">
                  {user.userName}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {user.userEmail}
                </p>
              </div>

              {/* Activity count with progress */}
              <div className="flex flex-col items-end gap-1">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {(user.count ?? 0).toLocaleString()}
                </span>
                <div className="w-20 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all"
                    style={{ width: `${((user.count ?? 0) / maxCount) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Users className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No user data available</p>
        </div>
      )}
    </FluentCard>
  )
}
